import {Badge} from '@orbit-ui/components/badge';
import {Button} from '@orbit-ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@orbit-ui/components/card';
import {Input} from '@orbit-ui/components/input';
import {PageHeader} from '@orbit-ui/components/page-header';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@orbit-ui/components/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@orbit-ui/components/sheet';
import {
  SearchIcon,
  StarIcon,
  ServerIcon,
  DatabaseIcon,
  ShieldCheckIcon,
  ActivityIcon,
  CheckIcon,
  ClockIcon,
  UsersIcon,
  GlobeIcon,
  CodeIcon,
  ExternalLink as GithubIcon,
} from 'lucide-react';
// Use a simple icon as replacement for GithubIcon
import * as React from 'react';

// Define template interface
type Template = {
  id: number;
  name: string;
  description: string;
  language: string;
  version: string;
  lastUpdated: string;
  isApproved: boolean;
  tags?: string[];
  stars: number;
  committers?: number;
  repository: string;
  commitHash: string;
  usageCount: number;
  productionUsage: number;
  overview: string;
  interfaces: string[];
  reviews: Array<{user: string; rating: number; comment: string}>;
  capabilities: string[];
  securityFeatures: string[];
  approvalStatus: string;
};

// Template data (in a real app this would come from an API)
const templates: Template[] = [
  {
    id: 1,
    name: 'HTTP Service Template',
    description: 'Boilerplate for creating HTTP APIs with OpenAPI validation',
    language: 'TypeScript',
    version: '1.2.0',
    lastUpdated: '2024-03-15',
    isApproved: true,
    approvalStatus: 'Approved',
    usageCount: 152,
    productionUsage: 78,
    stars: 45,
    tags: ['api', 'http', 'rest'],
    committers: 7,
    repository: 'https://github.com/cosmonic/templates/http-service',
    commitHash: '8a7b3c9d',
    overview:
      'This template provides a comprehensive starting point for building HTTP services using the WebAssembly component model. It includes OpenAPI validation, error handling patterns, and standardized logging.',
    interfaces: ['cosmonic:httpserver/handler', 'wasi:logging/write'],
    reviews: [
      {user: 'Sarah M.', rating: 5, comment: 'Perfect starter for our API services.'},
      {user: 'John D.', rating: 4, comment: 'Great template, would like more examples.'},
    ],
    capabilities: ['httpserver'],
    securityFeatures: ['Input validation', 'Rate limiting', 'Audit logging'],
  },
  {
    id: 2,
    name: 'Database Interface Template',
    description: 'Template for database-backed components with connection pooling',
    language: 'Rust',
    version: '0.9.1',
    lastUpdated: '2024-03-01',
    isApproved: true,
    approvalStatus: 'Approved',
    usageCount: 87,
    productionUsage: 42,
    stars: 32,
    tags: ['database', 'persistence'],
    committers: 5,
    repository: 'https://github.com/cosmonic/templates/database-interface',
    commitHash: '5e2f1a8b',
    overview:
      'A foundation for building database-backed components that provide connection pooling, transactions, and query optimization.',
    interfaces: ['cosmonic:postgres/client', 'wasi:logging/write'],
    reviews: [
      {user: 'Alex T.', rating: 5, comment: 'Exactly what we needed for our database services.'},
      {user: 'Maria K.', rating: 4, comment: 'Would be nice to have Kafka support too.'},
    ],
    capabilities: ['postgres'],
    securityFeatures: ['Prepared statements', 'Connection encryption'],
  },
  {
    id: 3,
    name: 'Event Handler Template',
    description: 'Template for creating event-driven micro components',
    language: 'Rust',
    version: '1.0.0',
    lastUpdated: '2024-02-20',
    isApproved: true,
    approvalStatus: 'Approved',
    usageCount: 65,
    productionUsage: 38,
    stars: 27,
    tags: ['events', 'messaging'],
    committers: 4,
    repository: 'https://github.com/cosmonic/templates/event-handler',
    commitHash: '3c6d9e4f',
    overview:
      'A template for building event-driven components that process messages from various sources. Includes patterns for error handling, retries, and dead letter queues.',
    interfaces: ['cosmonic:messaging/consumer', 'cosmonic:messaging/producer'],
    reviews: [
      {user: 'David L.', rating: 4, comment: 'Works well but could use more documentation.'},
      {user: 'Elena R.', rating: 5, comment: 'Saved us weeks of work on event processing.'},
    ],
    capabilities: ['messaging'],
    securityFeatures: ['Dead letter queues', 'Payload validation'],
  },
  {
    id: 4,
    name: 'Data Processor Template',
    description: 'Template for data transformation pipelines',
    language: 'TypeScript',
    version: '0.8.5',
    lastUpdated: '2024-02-10',
    isApproved: false,
    approvalStatus: 'Under Review',
    usageCount: 23,
    productionUsage: 0,
    stars: 11,
    tags: ['data', 'transformation'],
    committers: 3,
    repository: 'https://github.com/cosmonic/templates/data-processor',
    commitHash: '7b2a5d1c',
    overview:
      'Designed specifically for data transformation pipelines that require data processing, filtering, and aggregation.',
    interfaces: ['cosmonic:httpserver/handler', 'cosmonic:messaging/consumer'],
    reviews: [
      {user: 'Chris P.', rating: 4, comment: 'Great for our React frontends.'},
      {user: 'Tina M.', rating: 5, comment: 'Simplifies our frontend integrations significantly.'},
    ],
    capabilities: ['httpserver', 'messaging'],
    securityFeatures: ['CORS configuration', 'XSS protection', 'CSRF tokens'],
  },
  {
    id: 5,
    name: 'ML Inference Template',
    description: 'Template for deploying ML models as WebAssembly components',
    language: 'Python',
    version: '0.7.1',
    lastUpdated: '2024-01-30',
    isApproved: false,
    approvalStatus: 'In Development',
    usageCount: 8,
    productionUsage: 0,
    stars: 6,
    tags: ['ml', 'ai', 'inference'],
    committers: 2,
    repository: 'https://github.com/cosmonic/templates/ml-inference',
    commitHash: '1e4f8a3b',
    overview:
      'A specialized template for deploying machine learning models as WebAssembly components. Provides interfaces for model loading, inference, and metrics reporting.',
    interfaces: ['cosmonic:task/handler', 'wasi:filesystem/readwrite'],
    reviews: [
      {user: 'Robert J.', rating: 3, comment: 'Needs more work on model optimization.'},
      {user: 'Lisa Q.', rating: 4, comment: 'Good start for our ML deployment needs.'},
    ],
    capabilities: ['task', 'storage'],
    securityFeatures: ['Input sanitization', 'Resource limits'],
  },
];

// Helper function to render star ratings
const StarRating = ({rating}: {rating: number}) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;

  return (
    <div className="flex items-center">
      {Array.from({length: 5}).map((_, index) => (
        <StarIcon
          key={index}
          className={`size-4 ${
            index < fullStars
              ? 'fill-yellow-500 text-yellow-500'
              : index === fullStars && hasHalfStar
                ? 'fill-yellow-500 text-yellow-500'
                : 'text-muted-foreground'
          }`}
        />
      ))}
      <span className="ml-1 text-sm font-medium">{rating.toFixed(1)}</span>
    </div>
  );
};

// Language icon mapping
const getLanguageIcon = (language: string) => {
  switch (language.toLowerCase()) {
    case 'rust': {
      return (
        <Badge className="bg-orange-700" variant="secondary">
          Rust
        </Badge>
      );
    }

    case 'typescript': {
      return (
        <Badge className="bg-blue-600" variant="secondary">
          TypeScript
        </Badge>
      );
    }

    case 'python': {
      return (
        <Badge className="bg-blue-800" variant="secondary">
          Python
        </Badge>
      );
    }

    default: {
      return <Badge variant="secondary">{language}</Badge>;
    }
  }
};

// Capability icon mapping
const getCapabilityIcon = (capability: string) => {
  switch (capability) {
    case 'httpserver': {
      return <ServerIcon className="size-4 text-blue-500" aria-label="HTTP Server" />;
    }

    case 'postgres': {
      return <DatabaseIcon className="size-4 text-indigo-500" aria-label="PostgreSQL" />;
    }

    case 'messaging': {
      return <ActivityIcon className="size-4 text-green-500" aria-label="Messaging" />;
    }

    case 'task': {
      return <ClockIcon className="size-4 text-amber-500" aria-label="Task Scheduler" />;
    }

    case 'storage': {
      return <DatabaseIcon className="size-4 text-slate-500" aria-label="Storage" />;
    }

    default: {
      return <CodeIcon className="size-4 text-gray-500" aria-label={capability} />;
    }
  }
};

export const ComponentsTemplatesView = () => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedLanguage, setSelectedLanguage] = React.useState('all');
  const [selectedTemplate, setSelectedTemplate] = React.useState<Template | null>(null);
  const [isSheetOpen, setIsSheetOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('overview');

  // Filter templates based on search term and language
  const filteredTemplates = templates.filter((template) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesLanguage =
      selectedLanguage === 'all' ||
      template.language.toLowerCase() === selectedLanguage.toLowerCase();

    return matchesSearch && matchesLanguage;
  });

  // Group templates by approval status
  const approvedTemplates = filteredTemplates.filter((t) => t.isApproved);
  const otherTemplates = filteredTemplates.filter((t) => !t.isApproved);

  // Handle template selection and sheet open/close
  const handleTemplateClick = (template: Template) => {
    setSelectedTemplate(template);
    setIsSheetOpen(true);
  };

  return (
    <div className="container mx-auto space-y-8 py-6">
      <PageHeader
        title="Component Templates"
        description="Discover and use organization-approved templates for creating new components"
        action={
          <Button className="gap-2">
            <PlusIcon className="size-4" />
            Create Template
          </Button>
        }
      />

      {/* Search and Filter Controls */}
      <div className="flex flex-col items-start gap-4 md:flex-row md:items-center">
        <div className="relative w-full md:w-96">
          <SearchIcon className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search templates..."
            className="pl-8"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <div className="w-full md:w-64">
          <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by language" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Languages</SelectItem>
              <SelectItem value="rust">Rust</SelectItem>
              <SelectItem value="typescript">TypeScript</SelectItem>
              <SelectItem value="python">Python</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Organization Approved Templates Section */}
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <CheckIcon className="size-5 text-green-500" />
          <h2 className="text-xl font-semibold">Organization Approved Templates</h2>
        </div>

        {approvedTemplates.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {approvedTemplates.map((template) => (
              <Card
                key={template.id}
                className="flex h-full cursor-pointer flex-col transition-colors hover:border-primary/50"
                onClick={() => handleTemplateClick(template)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base font-semibold">{template.name}</CardTitle>
                      <CardDescription className="text-xs">
                        Version {template.version}
                      </CardDescription>
                    </div>
                    {getLanguageIcon(template.language)}
                  </div>
                </CardHeader>
                <CardContent className="grow pb-2">
                  <p className="mb-2 line-clamp-2 text-sm">{template.description}</p>
                  <div className="mb-3 mt-2 flex flex-wrap gap-1">
                    {template.capabilities.map((cap) => (
                      <div key={cap} className="inline-flex">
                        {getCapabilityIcon(cap)}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <UsersIcon className="size-3" />
                      <span>{template.usageCount} components</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <GlobeIcon className="size-3" />
                      <span>{template.productionUsage} in production</span>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="border-t pt-2">
                  <StarRating rating={template.stars} />
                </CardFooter>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-6">
            <div className="text-center text-muted-foreground">
              No approved templates match your criteria
            </div>
          </Card>
        )}
      </div>

      {/* Other Templates Section */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold">Other Templates</h2>

        {otherTemplates.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {otherTemplates.map((template) => (
              <Card
                key={template.id}
                className="flex h-full cursor-pointer flex-col transition-colors hover:border-primary/50"
                onClick={() => handleTemplateClick(template)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base font-semibold">{template.name}</CardTitle>
                      <CardDescription className="text-xs">
                        Version {template.version}
                      </CardDescription>
                    </div>
                    {getLanguageIcon(template.language)}
                  </div>
                </CardHeader>
                <CardContent className="grow pb-2">
                  <p className="mb-2 line-clamp-2 text-sm">{template.description}</p>
                  <div className="mb-3 mt-2 flex flex-wrap gap-1">
                    {template.capabilities.map((cap) => (
                      <div key={cap} className="inline-flex">
                        {getCapabilityIcon(cap)}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <UsersIcon className="size-3" />
                      <span>{template.usageCount} components</span>
                    </div>
                    {template.productionUsage > 0 ? (
                      <div className="flex items-center gap-1">
                        <GlobeIcon className="size-3" />
                        <span>{template.productionUsage} in production</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" className="px-1 text-[10px]">
                          Not in production
                        </Badge>
                      </div>
                    )}
                  </div>
                </CardContent>
                <CardFooter className="border-t pt-2">
                  <StarRating rating={template.stars} />
                </CardFooter>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-6">
            <div className="text-center text-muted-foreground">
              No other templates match your criteria
            </div>
          </Card>
        )}
      </div>

      {/* Detail Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl md:max-w-2xl">
          {selectedTemplate && (
            <>
              <SheetHeader className="space-y-1">
                <div className="flex items-center justify-between">
                  <SheetTitle>{selectedTemplate.name}</SheetTitle>
                  {getLanguageIcon(selectedTemplate.language)}
                </div>
                <SheetDescription>
                  Version {selectedTemplate.version} - Last updated {selectedTemplate.lastUpdated}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Version and Repository Info */}
                <div className="flex flex-col gap-4 text-sm md:flex-row">
                  <div className="flex items-center gap-2">
                    <CodeIcon className="size-4 text-muted-foreground" />
                    <span>Commit: {selectedTemplate.commitHash}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <GithubIcon className="size-4" />
                    <a
                      href={selectedTemplate.repository}
                      target="_blank"
                      className="text-blue-500 hover:underline"
                      rel="noopener"
                    >
                      View Repository
                    </a>
                  </div>
                </div>

                {/* Usage Stats */}
                <div className="grid grid-cols-2 gap-4 rounded-lg bg-muted/50 p-4">
                  <div>
                    <div className="text-sm text-muted-foreground">
                      Components using this template
                    </div>
                    <div className="text-xl font-semibold">{selectedTemplate.usageCount}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">In production</div>
                    <div className="text-xl font-semibold">{selectedTemplate.productionUsage}</div>
                  </div>
                </div>

                {/* Template Interfaces */}
                {selectedTemplate.interfaces && selectedTemplate.interfaces.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium">Interfaces</h3>
                    <div className="grid gap-2">
                      {selectedTemplate.interfaces.map((intf: string, index: number) => (
                        <div key={index} className="rounded-md border p-3">
                          <div className="font-medium">{intf}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tabs for different sections */}
                <div className="mt-6">
                  <div className="flex border-b">
                    <button
                      className={`px-4 py-2 text-sm font-medium ${activeTab === 'overview' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'}`}
                      onClick={() => setActiveTab('overview')}
                    >
                      Overview
                    </button>
                    <button
                      className={`px-4 py-2 text-sm font-medium ${activeTab === 'capabilities' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'}`}
                      onClick={() => setActiveTab('capabilities')}
                    >
                      Capabilities
                    </button>
                    <button
                      className={`px-4 py-2 text-sm font-medium ${activeTab === 'security' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'}`}
                      onClick={() => setActiveTab('security')}
                    >
                      Security
                    </button>
                  </div>

                  {activeTab === 'overview' && (
                    <div className="mt-4 space-y-4">
                      <div>
                        <h4 className="mb-2 font-medium">Description</h4>
                        <p className="text-sm">{selectedTemplate.overview}</p>
                      </div>

                      <div>
                        <h4 className="mb-2 font-medium">Implemented Interfaces</h4>
                        <div className="space-y-2">
                          {selectedTemplate.interfaces.map((intf: string) => (
                            <div key={intf} className="flex items-center gap-2 text-sm">
                              <CodeIcon className="size-4 text-blue-500" />
                              <code className="rounded bg-muted p-1 text-xs">{intf}</code>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="mb-2 font-medium">Ratings & Reviews</h4>
                        <div className="mb-3 flex items-center gap-2">
                          <StarRating rating={selectedTemplate.stars} />
                          <span className="text-sm text-muted-foreground">
                            from {selectedTemplate.reviews.length} reviews
                          </span>
                        </div>

                        <div className="space-y-3">
                          {selectedTemplate.reviews.map(
                            (
                              review: {user: string; rating: number; comment: string},
                              index: number,
                            ) => (
                              <div key={index} className="rounded-lg bg-muted/30 p-3">
                                <div className="mb-1 flex items-center justify-between">
                                  <div className="text-sm font-medium">{review.user}</div>
                                  <div className="flex">
                                    {Array.from({length: 5}).map((_, index_) => (
                                      <StarIcon
                                        key={index_}
                                        className={`size-3 ${index_ < review.rating ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground'}`}
                                      />
                                    ))}
                                  </div>
                                </div>
                                <p className="text-sm">{review.comment}</p>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'capabilities' && (
                    <div className="mt-4 space-y-4">
                      <div>
                        <h4 className="mb-3 font-medium">Required Capabilities</h4>
                        <div className="space-y-3">
                          {selectedTemplate.capabilities.map((cap: string) => (
                            <div key={cap} className="rounded-lg bg-muted/30 p-3">
                              <div className="mb-2 flex items-center gap-2">
                                {getCapabilityIcon(cap)}
                                <span className="text-sm font-medium capitalize">{cap}</span>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {cap === 'httpserver' &&
                                  'HTTP server capability with OpenAPI validation and routing.'}
                                {cap === 'postgres' &&
                                  'PostgreSQL database access with connection pooling and transactions.'}
                                {cap === 'messaging' &&
                                  'Asynchronous messaging with publish/subscribe capabilities.'}
                                {cap === 'task' &&
                                  'Scheduled task execution with cron-like scheduling.'}
                                {cap === 'storage' &&
                                  'Persistent storage access for files and objects.'}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="mb-2 font-medium">Configuration Schema</h4>
                        <div className="rounded-lg bg-muted/30 p-3">
                          <code className="block whitespace-pre-wrap text-xs">
                            {`{
  "type": "object",
  "properties": {
    "settings": {
      "type": "object",
      "properties": {
        "timeout": { "type": "integer", "default": 30000 },
        "retries": { "type": "integer", "default": 3 }
      }
    }
  }
}`}
                          </code>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'security' && (
                    <div className="mt-4 space-y-4">
                      <div>
                        <h4 className="mb-3 font-medium">Security Features</h4>
                        <div className="space-y-2">
                          {selectedTemplate.securityFeatures.map(
                            (feature: string, index: number) => (
                              <div
                                key={index}
                                className="flex items-center gap-2 rounded-lg bg-muted/30 p-2"
                              >
                                <ShieldCheckIcon className="size-4 text-green-500" />
                                <span className="text-sm">{feature}</span>
                              </div>
                            ),
                          )}
                        </div>
                      </div>

                      <div>
                        <h4 className="mb-3 font-medium">Security Audit Status</h4>
                        <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                          <div className="flex items-center gap-2">
                            <CheckIcon className="size-4 text-green-500" />
                            <span className="text-sm font-medium text-green-700">
                              Security audit passed
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-green-600">
                            Last audit: 2023-10-20 by Security Team
                          </p>
                        </div>
                      </div>

                      <div>
                        <h4 className="mb-2 font-medium">Capability Access</h4>
                        <p className="mb-2 text-sm text-muted-foreground">
                          This template requires the following capability access:
                        </p>
                        <ul className="list-disc space-y-1 pl-5 text-sm">
                          {selectedTemplate.capabilities.map((cap: string) => (
                            <li key={cap}>
                              <code className="rounded bg-muted px-1 py-0.5 text-xs">{`cosmonic:${cap}`}</code>
                              {cap === 'httpserver' &&
                                ' - HTTP server with restricted ports (80, 443, 8080)'}
                              {cap === 'postgres' && ' - Read/write access to application database'}
                              {cap === 'messaging' && ' - Publish/subscribe to application topics'}
                              {cap === 'task' && ' - Schedule and execute periodic tasks'}
                              {cap === 'storage' && ' - Read/write to application storage buckets'}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {/* Reviews */}
                {selectedTemplate.reviews && selectedTemplate.reviews.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium">User Reviews</h3>
                    <div className="space-y-3">
                      {selectedTemplate.reviews.map((review, index) => (
                        <div key={index} className="rounded border p-3">
                          <div className="flex items-center justify-between">
                            <div className="font-medium">{review.user}</div>
                            <StarRating rating={review.rating} />
                          </div>
                          <p className="mt-2 text-sm">{review.comment}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-6 flex justify-end gap-3">
                  <Button variant="outline">View All Versions</Button>
                  <Button>Use This Template</Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

function PlusIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </svg>
  );
}
